import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import SEOHead from "@/components/SEOHead";
import SearchHeader from "@/components/search-header";
import Footer from "@/components/footer";
import { loadAllArtworks, searchArtworks, getRandomArtwork, StaticArtwork } from "@/lib/static-data";
import { trackEvent } from "@/lib/analytics";
import { loadPageData } from "@/lib/hubs";
import { getPreloadedData } from "@/lib/hydration";
import EmotionMiniBars, { EmotionProfile } from "@/components/emotion-mini-bars";
import { fmtCount, hubIndexDetails, hubPath, hubTitle, HUB_INDEX_RELATED, HUB_KINDS, type Hub, type HubIndexEntry, type HubKind, type HubLink } from "@shared/hubs";

function useSearch(source: string) {
  const [, setLocation] = useLocation();
  const [all, setAll] = useState<StaticArtwork[]>([]);
  const [results, setResults] = useState<StaticArtwork[]>([]);
  useEffect(() => {
    loadAllArtworks().then(setAll).catch(() => setAll([]));
  }, []);
  return {
    header: (
      <SearchHeader
        artworks={results}
        onArtworkSelect={(id: number) => {
          setLocation(`/artwork/${id}/`);
          trackEvent("view_artwork", "navigation", source, id);
        }}
        onSearch={(q: string) => {
          if (!q.trim()) return setResults([]);
          const r = searchArtworks(all, q, 20);
          setResults(r);
          trackEvent("search", "artwork_search", q, r.length);
        }}
        totalCount={all.length}
        onRandomArtwork={() => {
          const a = getRandomArtwork(all);
          if (a?.id) setLocation(`/artwork/${a.id}/`);
        }}
      />
    ),
  };
}

function LinkChips({ kind, links }: { kind: HubKind; links: HubLink[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {links.map((l) => (
        // Plain links: hub pages load their data from the page HTML.
        <a
          key={l.slug}
          href={hubPath(kind, l.slug)}
          className="px-3 py-1 rounded-full border text-sm hover:border-indigo-300 hover:text-indigo-700 transition-colors"
        >
          {l.name} <span className="text-muted-foreground">({fmtCount(l.count)})</span>
        </a>
      ))}
    </div>
  );
}

export default function HubPage({ kind }: { kind: HubKind }) {
  const [location] = useLocation();
  const [hub, setHub] = useState<Hub | null>(null);
  const [missing, setMissing] = useState(false);
  const { header } = useSearch(`hub_${kind}`);

  useEffect(() => {
    let cancelled = false;
    setHub(null);
    setMissing(false);
    loadPageData<Hub>("hub", location).then((h) => {
      if (cancelled) return;
      if (h) setHub(h);
      else setMissing(true);
    });
    return () => {
      cancelled = true;
    };
  }, [location]);

  const { indexPath, indexName, related, seeAlso, cardShowsArtist } = HUB_KINDS[kind];

  return (
    <div className="min-h-screen bg-background">
      {hub && (
        <SEOHead
          title={hubTitle(hub)}
          description={hub.intro}
          url={`https://ariadnesgallery.com${hubPath(kind, hub.slug)}`}
          type="website"
        />
      )}
      {header}
      <main className="pt-16">
        {missing && (
          <div className="max-w-4xl mx-auto px-4 py-12 text-center">
            <p className="text-muted-foreground">This page could not be loaded.</p>
            <a href={indexPath} className="mt-4 inline-block text-indigo-600 hover:underline">
              Browse all {indexName.toLowerCase()}
            </a>
          </div>
        )}
        {!hub && !missing && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse text-muted-foreground">Loading...</div>
          </div>
        )}
        {hub && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-4">
              <a href="/" className="hover:underline">Home</a> /{" "}
              <a href={indexPath} className="hover:underline">{indexName}</a>
            </nav>
            <h1 className="text-3xl sm:text-4xl font-semibold mb-4">{hub.name}</h1>
            <p className="text-lg text-muted-foreground max-w-3xl mb-8">{hub.intro}</p>

            {hub.profile?.length > 0 && (
              <section className="mb-8">
                <h2 className="text-lg font-semibold mb-3">Emotional profile</h2>
                <EmotionProfile profile={hub.profile} />
              </section>
            )}

            {hub.related.length > 0 && (
              <section className="mb-8">
                <h2 className="text-lg font-semibold mb-3">{related.heading}</h2>
                <LinkChips kind={related.kind} links={hub.related} />
              </section>
            )}

            <section className="mb-10">
              <h2 className="text-lg font-semibold mb-3">
                Artworks
                {hub.count > hub.artworks.length && (
                  <span className="text-muted-foreground font-normal"> ({hub.artworks.length} of {fmtCount(hub.count)})</span>
                )}
              </h2>
              <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {hub.artworks.map((a) => (
                  <li key={a.id}>
                    <Link
                      href={`/artwork/${a.id}/`}
                      className="block group"
                      onClick={() => trackEvent("view_artwork", "navigation", `hub_${kind}_${hub.slug}`, a.id)}
                    >
                      {a.imageUrl ? (
                        <img
                          src={a.imageUrl}
                          alt={`${a.title} by ${a.artist}`}
                          loading="lazy"
                          className="w-full aspect-square object-cover rounded bg-gray-100"
                        />
                      ) : (
                        <div className="w-full aspect-square rounded bg-gray-100" />
                      )}
                      <div className="mt-2 text-sm font-medium group-hover:text-indigo-700 line-clamp-2">{a.title}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {cardShowsArtist ? a.artist : ""}
                        {cardShowsArtist && a.date ? ", " : ""}
                        {a.date ?? ""}
                      </div>
                      <EmotionMiniBars emotions={a.emotions} className="mt-1.5" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            {hub.seeAlso.length > 0 && (
              <section className="mb-8">
                <h2 className="text-lg font-semibold mb-3">{seeAlso.heading}</h2>
                <LinkChips kind={seeAlso.kind} links={hub.seeAlso} />
              </section>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

interface HubList {
  kind: HubKind;
  title: string;
  description: string;
  intro?: string;
  entries: HubIndexEntry[];
}

const BROWSE_LINKS = [
  { href: "/artists/", label: "Artists A–Z" },
  { href: "/styles/", label: "Art styles and movements" },
  { href: "/museums/", label: "Museums and galleries" },
  { href: "/emotion/", label: "Art by emotion" },
];

export function HubIndexPage({ kind }: { kind: HubKind }) {
  const { indexPath, indexName, filterPlaceholder, related: relatedCfg } = HUB_KINDS[kind];
  const [location] = useLocation();
  // On a full page load the list is in the page, so the first render already
  // shows it (no empty page while it loads).
  const [list, setList] = useState<HubList | null>(() => {
    const preloaded = getPreloadedData()?.hubList as HubList | undefined;
    return preloaded?.kind === kind ? preloaded : null;
  });
  const [filter, setFilter] = useState("");
  const { header } = useSearch(`hub_index_${kind}`);

  useEffect(() => {
    if (list?.kind === kind) return;
    loadPageData<HubList>("hubList", location).then(setList);
  }, [location, kind]);

  const entries = (list?.entries ?? []).filter((e) => e.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="min-h-screen bg-background">
      {list && (
        <SEOHead
          title={`${list.title} — Ariadne's Gallery`}
          description={list.description}
          url={`https://ariadnesgallery.com${indexPath}`}
          type="website"
        />
      )}
      {header}
      <main className="pt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h1 className="text-3xl sm:text-4xl font-semibold mb-4">{list?.title ?? ""}</h1>
          <p className="text-lg text-muted-foreground max-w-3xl mb-3">{list?.description ?? ""}</p>
          {list?.intro && <p className="text-muted-foreground max-w-3xl mb-6">{list.intro}</p>}
          <h2 className="text-lg font-semibold mb-3">All {indexName.toLowerCase()}</h2>
          {filterPlaceholder && (
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={filterPlaceholder}
              className="mb-6 w-full sm:w-80 border rounded-lg px-3 py-2"
            />
          )}
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
            {entries.map((e) => (
              <li key={e.slug}>
                <a href={hubPath(kind, e.slug)} className="hover:text-indigo-700 hover:underline">
                  {e.name}
                </a>{" "}
                <span className="text-sm text-muted-foreground">({hubIndexDetails(e)})</span>
                {e.related?.length ? (
                  <div className="text-sm text-muted-foreground">
                    {HUB_INDEX_RELATED[kind].label ? HUB_INDEX_RELATED[kind].label.charAt(0).toUpperCase() + HUB_INDEX_RELATED[kind].label.slice(1) : ""}
                    {e.related.map((r, i) => (
                      <span key={r.slug}>
                        {i > 0 ? ", " : ""}
                        <a href={hubPath(relatedCfg.kind, r.slug)} className="hover:text-indigo-700 hover:underline">
                          {r.name}
                        </a>
                      </span>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          <nav aria-label="Browse the collection" className="mt-10">
            <h2 className="text-lg font-semibold mb-3">Browse the collection</h2>
            <ul className="flex flex-wrap gap-2">
              {BROWSE_LINKS.filter((l) => l.href !== indexPath).map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="px-3 py-1 rounded-full border text-sm hover:border-indigo-300 hover:text-indigo-700 transition-colors">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </main>
      <Footer />
    </div>
  );
}
